import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Button, Divider, useTheme, useMediaQuery,
  Card, CardContent, Container, Chip, Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useCart } from '../context/CartContext';
import { CartRecommendations, PersonalizedRecommendations } from '../components/Recommendations';
import api from '../services/api';

// Cart component with responsive design
const Cart = () => {
  const { cart, loading, updateQuantity, removeFromCart, clearCart } = useCart();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // 運費設定
  const [shippingConfig, setShippingConfig] = useState({ base_fee: 60, free_shipping_threshold: 0 });

  // 載入運費設定
  useEffect(() => {
    const loadShippingConfig = async () => {
      try {
        const res = await api.get('/settings/shipping');
        if (res.data.success) {
          setShippingConfig({
            base_fee: parseInt(res.data.config.base_fee) || 60,
            free_shipping_threshold: parseInt(res.data.config.free_shipping_threshold) || 0
          });
        }
      } catch (error) {
        console.error('載入運費設定失敗:', error);
      }
    };
    loadShippingConfig();
  }, []);

  // 計算運費 (滿額免運)
  const shippingFee = shippingConfig.free_shipping_threshold > 0 && cart.subtotal >= shippingConfig.free_shipping_threshold
    ? 0
    : shippingConfig.base_fee;
  const total = cart.subtotal + shippingFee;

  // 計算總共省了多少 (原價 - 售價)
  const calculateTotalSavings = () => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((sum, item) => {
      const originalPrice = item.original_price || item.sale_price;
      const salePrice = item.display_price || item.sale_price;
      return sum + (originalPrice - salePrice) * item.quantity;
    }, 0);
  };
  const totalSavings = calculateTotalSavings();

  // GA4 view_cart event - track when cart page is viewed
  useEffect(() => {
    if (cart && cart.items && cart.items.length > 0) {
      const items = cart.items.map((item, index) => ({
        item_id: item.sku || item.product_id?.toString(),
        item_name: item.name,
        price: item.display_price || item.sale_price,
        quantity: item.quantity,
        index: index,
        item_variant: item.spec_info?.options
          ? Object.values(item.spec_info.options).join(' / ')
          : undefined
      }));

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'view_cart',
        ecommerce: {
          currency: 'TWD',
          value: cart.subtotal,
          items: items
        }
      });

      // Facebook Pixel ViewCart
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'ViewContent', {
          content_type: 'product',
          content_ids: cart.items.map(item => item.sku || item.product_id?.toString()),
          value: cart.subtotal,
          currency: 'TWD'
        });
      }
    }
  }, [cart]);

  // Handle remove from cart with GA4 tracking
  const handleRemoveFromCart = useCallback((item) => {
    // GA4 remove_from_cart event
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'remove_from_cart',
      ecommerce: {
        currency: 'TWD',
        value: (item.display_price || item.sale_price) * item.quantity,
        items: [{
          item_id: item.sku || item.product_id?.toString(),
          item_name: item.name,
          price: item.display_price || item.sale_price,
          quantity: item.quantity,
          item_variant: item.spec_info?.options
            ? Object.values(item.spec_info.options).join(' / ')
            : undefined
        }]
      }
    });

    // Call original removeFromCart（以購物車列 id 為鍵，支援多規格並存）
    removeFromCart(item.id);
  }, [removeFromCart]);

  // Handle begin checkout with GA4 tracking
  const handleBeginCheckout = useCallback(() => {
    if (cart && cart.items && cart.items.length > 0) {
      const items = cart.items.map((item, index) => ({
        item_id: item.sku || item.product_id?.toString(),
        item_name: item.name,
        price: item.display_price || item.sale_price,
        quantity: item.quantity,
        index: index,
        item_variant: item.spec_info?.options
          ? Object.values(item.spec_info.options).join(' / ')
          : undefined
      }));

      // GA4 begin_checkout event
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          currency: 'TWD',
          value: cart.subtotal,
          items: items
        }
      });

      // Facebook Pixel InitiateCheckout
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'InitiateCheckout', {
          content_type: 'product',
          content_ids: cart.items.map(item => item.sku || item.product_id?.toString()),
          num_items: cart.itemCount,
          value: cart.subtotal,
          currency: 'TWD'
        });
      }
    }
    navigate('/checkout');
  }, [cart, navigate]);

  // Show loading state if cart data is not available
  if (!cart || cart.items === undefined) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography>載入購物車中...</Typography>
        </Box>
      </Container>
    );
  }

  if (cart.items.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <ShoppingCartIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            購物車是空的
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/products')}
            sx={{ mt: 2 }}
          >
            去逛逛
          </Button>
        </Box>
        {/* 推薦商品 */}
        <PersonalizedRecommendations limit={8} title="您可能喜歡" />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        購物車 ({cart.itemCount} 件商品)
      </Typography>

      {/* 商品列表 - 手機版使用卡片，桌面版使用表格 */}
      {isMobile ? (
        // 手機版卡片佈局
        <Box sx={{ mb: 3 }}>
          {cart.items.map((item) => (
            <Card key={item.id} sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box
                    component="img"
                    src={item.image_url || 'https://via.placeholder.com/80x80?text=商品'}
                    alt={item.name}
                    sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                    onClick={() => navigate(`/products/${item.product_id}`)}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { color: 'primary.main' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                      onClick={() => navigate(`/products/${item.product_id}`)}
                    >
                      {item.name}
                    </Typography>
                    {/* 顯示選擇的規格 */}
                    {item.spec_info && item.spec_info.options && (
                      <Box sx={{ mt: 0.5 }}>
                        {Object.entries(item.spec_info.options).map(([key, value]) => (
                          <Chip
                            key={key}
                            label={`${key}: ${value}`}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5, height: 20, fontSize: '0.7rem' }}
                          />
                        ))}
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography color="error" fontWeight="bold">
                        ${item.display_price || item.sale_price}
                      </Typography>
                      {item.spec_info && item.spec_info.optionsPrice > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          (含規格 +${item.spec_info.optionsPrice})
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textDecoration: 'line-through' }}
                      >
                        ${item.original_price}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveFromCart(item)}
                    disabled={loading}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #eee', borderRadius: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      disabled={loading || item.quantity <= 1}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ mx: 1.5, minWidth: 24, textAlign: 'center' }}>
                      {item.quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      disabled={loading || item.quantity >= item.stock}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography color="error" fontWeight="bold" variant="h6">
                    ${item.item_total}
                  </Typography>
                </Box>
                {item.quantity >= item.stock && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                    已達庫存上限
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        // 桌面版表格佈局
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell>商品</TableCell>
                <TableCell align="center">單價</TableCell>
                <TableCell align="center">數量</TableCell>
                <TableCell align="center">小計</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cart.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        component="img"
                        src={item.image_url || 'https://via.placeholder.com/80x80?text=商品'}
                        alt={item.name}
                        sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1 }}
                      />
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                          onClick={() => navigate(`/products/${item.product_id}`)}
                        >
                          {item.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          SKU: {item.sku}
                        </Typography>
                        {/* 顯示選擇的規格 */}
                        {item.spec_info && item.spec_info.options && (
                          <Box sx={{ mt: 0.5 }}>
                            {Object.entries(item.spec_info.options).map(([key, value]) => (
                              <Chip
                                key={key}
                                label={`${key}: ${value}`}
                                size="small"
                                sx={{ mr: 0.5, mb: 0.5, height: 20, fontSize: '0.7rem' }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography color="error" fontWeight="bold">
                      ${item.display_price || item.sale_price}
                    </Typography>
                    {item.spec_info && item.spec_info.optionsPrice > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        (含規格 +${item.spec_info.optionsPrice})
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textDecoration: 'line-through' }}
                    >
                      ${item.original_price}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        disabled={loading || item.quantity <= 1}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography sx={{ mx: 1, minWidth: 30, textAlign: 'center' }}>
                        {item.quantity}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        disabled={loading || item.quantity >= item.stock}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {item.quantity >= item.stock && (
                      <Typography variant="caption" color="warning.main">
                        已達庫存上限
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography color="error" fontWeight="bold">
                      ${item.item_total}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveFromCart(item)}
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 操作按鈕 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/products')}
        >
          繼續購物
        </Button>
        <Button
          variant="text"
          color="error"
          onClick={clearCart}
          disabled={loading}
        >
          清空購物車
        </Button>
      </Box>

      {/* 加購推薦 */}
      <CartRecommendations
        cartProductIds={cart.items.map(item => item.product_id)}
        limit={4}
      />

      {/* 結算區域 */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          訂單摘要
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography>商品小計</Typography>
          <Typography>${cart.subtotal.toLocaleString()}</Typography>
        </Box>

        {/* 省了多少提示（相對原價的差，非額外折扣，避免 -$XX 誤導用戶） */}
        {totalSavings > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, color: 'success.main' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocalOfferIcon sx={{ fontSize: 18 }} />
              <Typography color="success.main" sx={{ fontSize: '0.9rem' }}>比原價省</Typography>
            </Box>
            <Typography color="success.main" fontWeight="bold">
              ${Math.round(totalSavings).toLocaleString()}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography>運費</Typography>
          <Typography>
            {shippingFee === 0 ? (
              <Chip label="免運" size="small" color="success" />
            ) : (
              `$${shippingFee}`
            )}
          </Typography>
        </Box>

        {/* 免運提示 */}
        {shippingConfig.free_shipping_threshold > 0 && shippingFee > 0 && (
          <Alert severity="info" sx={{ my: 2, borderRadius: 2 }} icon={<LocalOfferIcon />}>
            再買 ${Math.round(shippingConfig.free_shipping_threshold - cart.subtotal).toLocaleString()} 即可享免運！
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              滿 ${shippingConfig.free_shipping_threshold.toLocaleString()} 免運費
            </Typography>
          </Alert>
        )}

        {/* 免運達成提示 */}
        {shippingConfig.free_shipping_threshold > 0 && shippingFee === 0 && (
          <Alert severity="success" sx={{ my: 2, borderRadius: 2 }}>
            已達免運門檻，省 ${shippingConfig.base_fee} 運費！
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">訂單總計</Typography>
          <Typography variant="h6" color="error" fontWeight="bold">
            ${total.toLocaleString()}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleBeginCheckout}
        >
          前往結帳
        </Button>
      </Paper>

      {/* 手機版底部固定結帳列 */}
      {isMobile && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 'calc(56px + env(safe-area-inset-bottom))',
            left: 0,
            right: 0,
            zIndex: 1099,
            p: 1.5,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid #eee',
            bgcolor: '#fff',
          }}
          elevation={4}
        >
          <Box>
            <Typography variant="body2" color="text.secondary">
              共 {cart.itemCount} 件
            </Typography>
            <Typography variant="h6" color="error" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
              ${total.toLocaleString()}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            onClick={handleBeginCheckout}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2,
              bgcolor: '#e53935',
              '&:hover': { bgcolor: '#c62828' },
            }}
          >
            前往結帳
          </Button>
        </Paper>
      )}
    </Container>
  );
};

export default Cart;
